<template>
  <div class="article-picture">
    <div
      class="article-picture__wrapp"
      :style="pictureWrapStyle"
    >
      <BasePicture
        :alt="elem.attributes.image.alt"
        :sizes="elem.attributes.image.sizes"
        :origin="elem.attributes.image.origin"
        class="article-picture__picture"
        :class="pictureClass"
        @load="setLoaded"
        @click="openFullscreenGallery"
      />

      <LazyloadPlaceholder
        v-if="!loaded"
        class="article-picture__placeholder"
      />
    </div>
    <div
      v-if="isCaption"
      class="article-picture__caption"
    >
      <TheContent
        v-for="content of parsedCaption"
        :key="content.id"
        :elem="content"
      />
    </div>
    <div
      v-if="description"
      class="article-picture__description"
    >
      <TheContent
        v-for="content of description"
        :key="content.id"
        :elem="content"
      />
    </div>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import type { TContent, TImageSize } from '~/types/common';
  import FullscreenGallery from '~/components/FullscreenGallery.vue';
  import TheContent from '~/components/TheContent';
  import LazyloadPlaceholder from '~/components/LazyloadPlaceholder.vue';
  import type { IContentImage } from '~/types/components/ArticlePicture';
  import { useModal } from '@devhacker/shared/plugins/modal/useModal';
  import { useSingleArticleStore } from '~/store/singleArticle';
  import BasePicture from '@devhacker/shared/features/BasePicture/BasePicture.vue';

  export default defineNuxtComponent({
    name: 'ArticlePicture',

    components: {
      BasePicture,
      TheContent,
      LazyloadPlaceholder,
    },

    props: {
      elem: {
        type: Object as PropType<IContentImage>,
        required: true,
      },

      isGallery: {
        type: Boolean as PropType<boolean>,
        default: false,
      },

      borderRadius: {
        type: Number as PropType<number>,
        default: 32,
      },
    },

    data: () => ({
      loaded: false,
      startLoading: false,
    }),

    computed: {
      article() {
        return useSingleArticleStore().article;
      },

      pictureWrapStyle(): Record<string, string> | null {
        return {
          borderRadius: `${this.borderRadius}px`,
        };
      },

      sizes(): TImageSize[] {
        return this.elem.attributes.image?.sizes;
      },

      alt(): string {
        return this.elem.attributes.image?.alt || '';
      },

      url(): string | null {
        const { image: picture } = this.elem.attributes;
        return picture?.origin || null;
      },

      bordered(): boolean {
        return this.elem.attributes.bordered;
      },

      isMobile(): boolean {
        return this.elem.attributes.isMobile;
      },

      parsedCaption(): TContent[] {
        return this.elem.attributes.parsed_caption;
      },

      description(): TContent[] {
        return this.elem.attributes.description;
      },

      pictureClass(): Record<string, boolean> {
        return {
          'article-picture__img--gallery': this.isGallery,
          'article-picture__img--bordered': this.bordered && !this.isMobile,
        };
      },

      isCaption(): boolean {
        return Boolean(!this.isMobile && this.parsedCaption.length);
      },
    },

    mounted() {
      setTimeout(() => {
        this.startLoading = true;
      }, 300);
    },

    methods: {
      openFullscreenGallery() {
        if (!this.isGallery) {
          useModal().open(
            FullscreenGallery,
            { activeSlide: this.url, closeClickOutside: true },
            {
              transition: 'modal-fade',
            },
          );
        }
      },
      setLoaded() {
        this.loaded = true;
      },
    },
  });
</script>

<style lang="scss" scoped>
  .article-picture {
    &__wrapp {
      position: relative;
      overflow: hidden;
    }

    &__placeholder {
      position: absolute;
      z-index: -1;
      top: 0;
      left: 0;
    }

    &__picture {
      cursor: pointer;
    }

    &__description {
      @include smallText;
      margin-top: $spacing-2;
      word-break: break-word;

      :deep() a {
        @include link-styles;
      }
    }

    &__caption {
      @include tinyText;
      margin-top: $spacing-2;
      word-break: break-word;
      color: $text-color--1;
      position: relative;
      :deep() a {
        @include link-styles;
      }
    }

    &__img {
      &--gallery {
        cursor: pointer;
        border-radius: 32px;
        overflow: hidden;
        // * когда будет доступ к апи, нужно будет пошаманить над размерами изображения
        width: 100%;
        height: 320px;

        :deep() img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      &--bordered {
        :deep() {
          img {
            border-radius: 32px;
            border: 1px solid $stroke-color--1;
          }
        }
      }
    }
  }
</style>
